import { rescaleElements } from './label-rescale';
import { tokensUsed } from './tokens';

/**
 * The edit vocabulary the label design assistant speaks, shared by the server
 * (which validates each operation against an in-memory draft as the model
 * proposes it) and the web editor (which replays the accepted ones onto the
 * operator's unsaved draft as one undoable step). One implementation, so what
 * the server accepted is exactly what the canvas shows.
 *
 * Deliberately structural — it knows boxes, inches and element types, not how
 * a label renders. The token catalog is an input ({@link LabelDraftRules}),
 * since it lives with the renderer in the web package.
 */

/** Every placeable label element type. Mirrors the web editor's catalog. */
export const LABEL_ELEMENT_TYPES = [
  'text',
  'image',
  'plantName',
  'subheader',
  'productName',
  'skuBarcode',
  'inventoryBarcode',
  'priceBarcode',
  'weightBarcode',
  'price',
  'totalPrice',
  'weight',
  'ingredients',
  'packedDate',
  'keepRefrigerated',
  'mark',
  'divider',
] as const;
export type LabelDraftElementType = (typeof LABEL_ELEMENT_TYPES)[number];

const ALIGNS = ['left', 'center', 'right'] as const;
const VALIGNS = ['top', 'middle', 'bottom'] as const;
const ROTATIONS = [0, 90, 180, 270] as const;
const IMAGE_SOURCES = [
  'vendor',
  'producer',
  'library',
  'legend',
  'custom',
] as const;
const FALLBACKS = ['vendor', 'none'] as const;
const CONDITIONS = ['always', 'usda', 'state', 'exempt'] as const;

/** One placed element — the structural fields the assistant may set. */
export interface LabelDraftElement {
  id: string;
  type: LabelDraftElementType;
  xIn: number;
  yIn: number;
  wIn: number;
  hIn: number;
  align: (typeof ALIGNS)[number];
  vAlign: (typeof VALIGNS)[number];
  scale: number;
  rotation?: number;
  template?: string;
  src?: string;
  source?: (typeof IMAGE_SOURCES)[number];
  fallback?: (typeof FALLBACKS)[number];
  assetId?: string;
  condition?: (typeof CONDITIONS)[number];
}

/** Sizing for one barcode (template-level, shared by every barcode of its
 *  kind on the label). */
export interface LabelDraftBarcodeSettings {
  heightPx: number;
  moduleWidth: number;
  showText: boolean;
}

/** A label stock (the physical paper) the template can print on. */
export interface LabelDraftStock {
  id: string;
  name: string;
  widthIn: number;
  heightIn: number;
  /** Preprinted stock reserves its header/footer bands. */
  preprinted?: boolean;
  headerMarginIn?: number;
  footerMarginIn?: number;
}

/** The part of a template the assistant edits: one label's canvas. */
export interface LabelDraftShape {
  size: { widthIn: number; heightIn: number };
  /** The stock it prints on, when known. */
  stockId?: string;
  /** SKU and inventory barcode sizing, when the caller edits it. */
  barcode?: {
    sku: LabelDraftBarcodeSettings;
    inventory: LabelDraftBarcodeSettings;
  };
  layout: {
    elements: LabelDraftElement[];
    /** Preprinted bands nothing may overlap (from the label stock). */
    headerMarginIn?: number;
    footerMarginIn?: number;
    leftMarginIn?: number;
    rightMarginIn?: number;
  };
}

/** The fields an add or update may set (never `id`, never `type` on update). */
export type LabelDraftElementFields = Partial<
  Omit<LabelDraftElement, 'id' | 'type'>
>;

export type LabelDraftOp =
  | {
      op: 'add_element';
      type: LabelDraftElementType;
      /** Assigned when the server accepts the op, so the client replays it
       *  with the same id the conversation referred to. */
      id?: string;
      fields: LabelDraftElementFields;
      /** Deliberately layer it over others (text on a logo) — otherwise an
       *  overlap is refused. */
      allowOverlap?: boolean;
    }
  | {
      op: 'update_element';
      id: string;
      fields: LabelDraftElementFields;
      allowOverlap?: boolean;
    }
  | { op: 'remove_element'; id: string }
  /** Line several elements up on one edge or centre line. */
  | {
      op: 'align_elements';
      ids: string[];
      edge: 'left' | 'right' | 'top' | 'bottom' | 'hcenter' | 'vcenter';
    }
  /** Space three or more elements evenly between the outermost two. */
  | {
      op: 'distribute_elements';
      ids: string[];
      axis: 'horizontal' | 'vertical';
    }
  /** Draw an element above (front) or beneath (back) everything else. */
  | { op: 'reorder_element'; id: string; to: 'front' | 'back' }
  /**
   * Move the template onto another stock (paper size) and/or orientation.
   * Every element is rescaled proportionally; the stock's preprinted bands
   * apply from then on.
   */
  | {
      op: 'set_stock';
      stockId: string;
      orientation: 'portrait' | 'landscape';
    }
  /** Resize one kind of barcode (all barcodes of that kind share it). */
  | {
      op: 'set_barcode';
      which: 'sku' | 'inventory';
      fields: Partial<LabelDraftBarcodeSettings>;
    };

/** What the validator holds every element to. */
export interface LabelDraftRules {
  /** Token names a text template may reference (uppercased). When absent,
   *  tokens aren't checked. */
  knownTokens?: ReadonlySet<string>;
  /** Smallest box side, in inches — below this nothing is legible or
   *  scannable. */
  minSideIn: number;
  /** Font multiplier bounds. */
  minScale: number;
  maxScale: number;
  /** The stocks `set_stock` may choose from; absent = it can't. */
  stocks?: LabelDraftStock[];
}

export const DEFAULT_LABEL_DRAFT_RULES: LabelDraftRules = {
  minSideIn: 0.1,
  minScale: 0.4,
  maxScale: 4,
};

/** Default box for a new element of each type (inches). */
const DEFAULT_BOX: Record<LabelDraftElementType, { wIn: number; hIn: number }> =
  {
    text: { wIn: 3.5, hIn: 0.4 },
    image: { wIn: 1.6, hIn: 0.5 },
    plantName: { wIn: 3.5, hIn: 0.34 },
    subheader: { wIn: 3.5, hIn: 0.2 },
    productName: { wIn: 3.5, hIn: 0.6 },
    skuBarcode: { wIn: 1.6, hIn: 0.55 },
    inventoryBarcode: { wIn: 3.5, hIn: 0.62 },
    priceBarcode: { wIn: 1.6, hIn: 0.6 },
    weightBarcode: { wIn: 1.6, hIn: 0.6 },
    price: { wIn: 3.5, hIn: 0.3 },
    totalPrice: { wIn: 1.6, hIn: 0.4 },
    weight: { wIn: 1.5, hIn: 0.6 },
    ingredients: { wIn: 3.5, hIn: 0.9 },
    packedDate: { wIn: 2.0, hIn: 0.25 },
    keepRefrigerated: { wIn: 3.5, hIn: 0.25 },
    mark: { wIn: 1.6, hIn: 0.5 },
    divider: { wIn: 3.5, hIn: 0.06 },
  };

/** Barcodes need room for their quiet zones to scan. */
const BARCODE_TYPES = new Set<LabelDraftElementType>([
  'skuBarcode',
  'inventoryBarcode',
  'priceBarcode',
  'weightBarcode',
]);
const MIN_BARCODE = { wIn: 0.9, hIn: 0.3 };

const EPS = 0.001;
const round = (n: number) => Math.round(n * 1000) / 1000;

/** The printable area: the label minus its preprinted bands. */
function printableArea(draft: LabelDraftShape) {
  const { layout, size } = draft;
  const left = layout.leftMarginIn ?? 0;
  const top = layout.headerMarginIn ?? 0;
  return {
    left,
    top,
    right: size.widthIn - (layout.rightMarginIn ?? 0),
    bottom: size.heightIn - (layout.footerMarginIn ?? 0),
  };
}

/**
 * The box an element actually covers on paper — a 90°/270° rotation turns it
 * about its centre, swapping its footprint's width and height.
 */
function footprint(el: LabelDraftElement) {
  const turned = el.rotation === 90 || el.rotation === 270;
  if (!turned) {
    return { x: el.xIn, y: el.yIn, w: el.wIn, h: el.hIn };
  }
  const cx = el.xIn + el.wIn / 2;
  const cy = el.yIn + el.hIn / 2;
  return { x: cx - el.hIn / 2, y: cy - el.wIn / 2, w: el.hIn, h: el.wIn };
}

/** How far two boxes must overlap (in) before it counts — touching edges and
 *  rounding don't. */
const OVERLAP_TOLERANCE = 0.02;

/** Whether two elements never print on the same package — a USDA legend and
 *  an exempt "Not for sale" line can share a spot. */
function mutuallyExclusive(a: LabelDraftElement, b: LabelDraftElement) {
  const ca = a.condition ?? 'always';
  const cb = b.condition ?? 'always';
  return ca !== 'always' && cb !== 'always' && ca !== cb;
}

/**
 * The other elements `el` covers on paper (by their real, rotated footprints),
 * ignoring ones it never prints alongside.
 * @param el - The element
 * @param elements - Everything on the label (`el` itself is skipped by id)
 * @returns The overlapped elements
 */
export function labelElementOverlaps(
  el: LabelDraftElement,
  elements: readonly LabelDraftElement[],
): LabelDraftElement[] {
  const a = footprint(el);
  return elements.filter((other) => {
    if (other.id === el.id || mutuallyExclusive(el, other)) return false;
    const b = footprint(other);
    const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
    const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
    return w > OVERLAP_TOLERANCE && h > OVERLAP_TOLERANCE;
  });
}

/**
 * Why `el` would newly cover something it didn't before, or null. Only NEW
 * overlaps count, so an element that already overlapped (an old template, a
 * deliberate layer) can still be edited.
 * @param el - The element as it would be
 * @param before - The element as it was (absent when being added)
 * @param elements - The label's elements as they'd be (incl. `el`)
 * @returns The reason, or null
 */
function newOverlapProblem(
  el: LabelDraftElement,
  before: LabelDraftElement | undefined,
  elements: readonly LabelDraftElement[],
): string | null {
  const already = new Set(
    before ? labelElementOverlaps(before, elements).map((o) => o.id) : [],
  );
  const hits = labelElementOverlaps(el, elements).filter(
    (o) => !already.has(o.id),
  );
  if (!hits.length) return null;
  const r = (n: number) => Math.round(n * 100) / 100;
  return `Overlaps ${hits
    .map(
      (o) =>
        `${o.type} "${o.id}" (x ${r(o.xIn)}–${r(o.xIn + o.wIn)}, y ${r(o.yIn)}–${r(o.yIn + o.hIn)})`,
    )
    .join(
      ', ',
    )}. Move or resize it into free space, shrink the other, or set allowOverlap only if the processor asked for layering.`;
}

/**
 * Why `el` can't go on the label, or null when it can. Every rule here is one
 * a printed label breaks visibly: off the paper, under a preprinted band, too
 * small to read or scan, or referencing data that doesn't exist.
 * @param el - The element as it would be
 * @param draft - The label it's on
 * @param rules - The limits
 * @returns The reason, or null
 */
export function labelElementProblem(
  el: LabelDraftElement,
  draft: LabelDraftShape,
  rules: LabelDraftRules = DEFAULT_LABEL_DRAFT_RULES,
): string | null {
  if (!LABEL_ELEMENT_TYPES.includes(el.type)) {
    return `Unknown element type "${el.type}".`;
  }
  for (const k of ['xIn', 'yIn', 'wIn', 'hIn', 'scale'] as const) {
    if (typeof el[k] !== 'number' || !Number.isFinite(el[k])) {
      return `${k} must be a number.`;
    }
  }
  if (!ALIGNS.includes(el.align)) return `align must be ${ALIGNS.join('/')}.`;
  if (!VALIGNS.includes(el.vAlign)) {
    return `vAlign must be ${VALIGNS.join('/')}.`;
  }
  if (
    el.rotation != null &&
    !ROTATIONS.includes(el.rotation as (typeof ROTATIONS)[number])
  ) {
    return 'rotation must be 0, 90, 180 or 270.';
  }
  if (el.scale < rules.minScale || el.scale > rules.maxScale) {
    return `scale must be between ${rules.minScale} and ${rules.maxScale}.`;
  }
  const minSide = el.type === 'divider' ? 0.02 : rules.minSideIn;
  if (el.wIn < minSide || el.hIn < minSide) {
    return `Too small — each side must be at least ${minSide}in.`;
  }
  if (
    BARCODE_TYPES.has(el.type) &&
    (el.wIn < MIN_BARCODE.wIn || el.hIn < MIN_BARCODE.hIn)
  ) {
    return `A barcode needs at least ${MIN_BARCODE.wIn}×${MIN_BARCODE.hIn}in to scan.`;
  }

  const area = printableArea(draft);
  const f = footprint(el);
  if (
    f.x < area.left - EPS ||
    f.y < area.top - EPS ||
    f.x + f.w > area.right + EPS ||
    f.y + f.h > area.bottom + EPS
  ) {
    return (
      `Outside the printable area — it must fit within x ${round(area.left)}–${round(area.right)}in, ` +
      `y ${round(area.top)}–${round(area.bottom)}in (the label minus its preprinted bands).`
    );
  }

  if (el.source && !IMAGE_SOURCES.includes(el.source)) {
    return `source must be ${IMAGE_SOURCES.join('/')}.`;
  }
  if (el.fallback && !FALLBACKS.includes(el.fallback)) {
    return `fallback must be ${FALLBACKS.join('/')}.`;
  }
  if (el.condition && !CONDITIONS.includes(el.condition)) {
    return `condition must be ${CONDITIONS.join('/')}.`;
  }
  if (el.type === 'text') {
    if (!el.template?.trim()) return 'A text block needs a template.';
    if (rules.knownTokens) {
      const unknown = tokensUsed(el.template).filter(
        (t) => !rules.knownTokens!.has(t),
      );
      if (unknown.length) {
        return `Unknown token${unknown.length > 1 ? 's' : ''}: ${unknown
          .map((t) => `{{${t}}}`)
          .join(', ')}.`;
      }
    }
  }
  return null;
}

/** An operation that went through, with the element(s) it touched. */
export interface AppliedLabelDraftOp {
  op: LabelDraftOp;
  /** The first (or only) element touched; '' for label-level ops. */
  elementId: string;
  elementIds: string[];
}

/** Barcode sizing limits — beyond these a printed code won't scan. */
const BARCODE_LIMITS = {
  heightPx: [16, 200],
  moduleWidth: [0.8, 3],
} as const;

/**
 * Applies moves (`{id → patch}`) to several elements at once, validating each
 * moved element; all-or-nothing, so a layout op never half-lands.
 */
function moveMany<T extends LabelDraftShape>(
  draft: T,
  patches: Map<string, Partial<LabelDraftElement>>,
  rules: LabelDraftRules,
): { ok: true; draft: T } | { ok: false; reason: string } {
  const elements = draft.layout.elements.map((e) =>
    patches.has(e.id) ? { ...e, ...patches.get(e.id) } : e,
  );
  for (const e of elements) {
    if (!patches.has(e.id)) continue;
    const before = draft.layout.elements.find((x) => x.id === e.id);
    const problem =
      labelElementProblem(e, draft, rules) ??
      newOverlapProblem(e, before, elements);
    if (problem) return { ok: false, reason: `${e.id}: ${problem}` };
  }
  return {
    ok: true,
    draft: { ...draft, layout: { ...draft.layout, elements } },
  };
}

/** The elements named by `ids`, or the first missing id. */
function pick(
  draft: LabelDraftShape,
  ids: string[],
): LabelDraftElement[] | string {
  const found: LabelDraftElement[] = [];
  for (const id of ids) {
    const el = draft.layout.elements.find((e) => e.id === id);
    if (!el) return id;
    found.push(el);
  }
  return found;
}

/** An operation that didn't, and why. */
export interface RejectedLabelDraftOp {
  op: LabelDraftOp;
  reason: string;
}

/**
 * Whether a layout of `size` is turned 90° on `stock` — the stock is defined
 * one way round and the template uses the other. Mirrors the editor.
 */
function isRotatedOnStock(
  size: { widthIn: number; heightIn: number },
  stock: { widthIn: number; heightIn: number },
): boolean {
  if (stock.widthIn === stock.heightIn || size.widthIn === size.heightIn) {
    return false;
  }
  return size.widthIn > size.heightIn !== stock.widthIn > stock.heightIn;
}

/**
 * The draft on a new stock and orientation: the canvas becomes the stock's
 * size in that orientation, every element rescales proportionally, and the
 * stock's preprinted bands land on the edges they cover in that orientation
 * (header/footer, or left/right when the layout is turned on the paper) — the
 * same placement the editor's stock picker makes.
 * @param draft - The draft
 * @param stock - The new paper
 * @param orientation - Portrait (taller) or landscape (wider)
 * @returns The draft on that stock
 */
export function placeOnLabelStock<T extends LabelDraftShape>(
  draft: T,
  stock: LabelDraftStock,
  orientation: 'portrait' | 'landscape',
): T {
  const lo = Math.min(stock.widthIn, stock.heightIn);
  const hi = Math.max(stock.widthIn, stock.heightIn);
  const size = {
    widthIn: orientation === 'landscape' ? hi : lo,
    heightIn: orientation === 'landscape' ? lo : hi,
  };
  const rotated = isRotatedOnStock(size, stock);
  const band = (v?: number) => (stock.preprinted ? (v ?? 0) : 0);
  const header = band(stock.headerMarginIn);
  const footer = band(stock.footerMarginIn);
  return {
    ...draft,
    stockId: stock.id,
    size,
    layout: {
      ...draft.layout,
      elements: rescaleElements(
        draft.layout.elements,
        { w: draft.size.widthIn, h: draft.size.heightIn },
        { w: size.widthIn, h: size.heightIn },
      ),
      headerMarginIn: rotated ? 0 : header,
      footerMarginIn: rotated ? 0 : footer,
      leftMarginIn: rotated ? header : 0,
      rightMarginIn: rotated ? footer : 0,
    },
  };
}

/**
 * Applies one operation, or explains why it can't. Pure: returns a new draft.
 * @param draft - The draft before
 * @param op - The operation
 * @param rules - The limits each element is held to
 * @param newId - Mints an id for an added element without one
 * @returns The new draft and the applied op, or the reason
 */
export function applyLabelDraftOp<T extends LabelDraftShape>(
  draft: T,
  op: LabelDraftOp,
  rules: LabelDraftRules,
  newId: () => string,
):
  | { ok: true; draft: T; applied: AppliedLabelDraftOp }
  | { ok: false; reason: string } {
  const els = draft.layout.elements;
  const withElements = (elements: LabelDraftElement[]): T => ({
    ...draft,
    layout: { ...draft.layout, elements },
  });

  switch (op.op) {
    case 'add_element': {
      if (!LABEL_ELEMENT_TYPES.includes(op.type)) {
        return { ok: false, reason: `Unknown element type "${op.type}".` };
      }
      const id = op.id ?? newId();
      if (els.some((e) => e.id === id)) {
        return { ok: false, reason: `An element with id "${id}" exists.` };
      }
      const box = DEFAULT_BOX[op.type];
      const el: LabelDraftElement = {
        id,
        type: op.type,
        xIn: 0,
        yIn: 0,
        wIn: box.wIn,
        hIn: box.hIn,
        align: 'left',
        vAlign: 'top',
        scale: 1,
        ...op.fields,
      };
      const problem =
        labelElementProblem(el, draft, rules) ??
        (op.allowOverlap
          ? null
          : newOverlapProblem(el, undefined, [...els, el]));
      if (problem) return { ok: false, reason: problem };
      return {
        ok: true,
        draft: withElements([...els, el]),
        applied: { op: { ...op, id }, elementId: id, elementIds: [id] },
      };
    }
    case 'update_element': {
      const current = els.find((e) => e.id === op.id);
      if (!current) return { ok: false, reason: `No element "${op.id}".` };
      const next = { ...current, ...op.fields, id: current.id };
      const nextEls = els.map((e) => (e.id === op.id ? next : e));
      const problem =
        labelElementProblem(next, draft, rules) ??
        (op.allowOverlap ? null : newOverlapProblem(next, current, nextEls));
      if (problem) return { ok: false, reason: problem };
      return {
        ok: true,
        draft: withElements(els.map((e) => (e.id === op.id ? next : e))),
        applied: { op, elementId: op.id, elementIds: [op.id] },
      };
    }
    case 'remove_element': {
      if (!els.some((e) => e.id === op.id)) {
        return { ok: false, reason: `No element "${op.id}".` };
      }
      return {
        ok: true,
        draft: withElements(els.filter((e) => e.id !== op.id)),
        applied: { op, elementId: op.id, elementIds: [op.id] },
      };
    }
    case 'align_elements': {
      const found = pick(draft, op.ids ?? []);
      if (typeof found === 'string') {
        return { ok: false, reason: `No element "${found}".` };
      }
      if (found.length < 2) {
        return { ok: false, reason: 'Align needs at least two elements.' };
      }
      const left = Math.min(...found.map((e) => e.xIn));
      const right = Math.max(...found.map((e) => e.xIn + e.wIn));
      const top = Math.min(...found.map((e) => e.yIn));
      const bottom = Math.max(...found.map((e) => e.yIn + e.hIn));
      const patches = new Map<string, Partial<LabelDraftElement>>();
      for (const e of found) {
        const patch: Partial<LabelDraftElement> =
          op.edge === 'left'
            ? { xIn: left }
            : op.edge === 'right'
              ? { xIn: right - e.wIn }
              : op.edge === 'top'
                ? { yIn: top }
                : op.edge === 'bottom'
                  ? { yIn: bottom - e.hIn }
                  : op.edge === 'hcenter'
                    ? { xIn: (left + right) / 2 - e.wIn / 2 }
                    : op.edge === 'vcenter'
                      ? { yIn: (top + bottom) / 2 - e.hIn / 2 }
                      : {};
        if (!Object.keys(patch).length) {
          return { ok: false, reason: `Unknown edge "${op.edge}".` };
        }
        patches.set(e.id, patch);
      }
      const res = moveMany(draft, patches, rules);
      if ('reason' in res) return res;
      return {
        ok: true,
        draft: res.draft,
        applied: { op, elementId: found[0].id, elementIds: op.ids },
      };
    }
    case 'distribute_elements': {
      const found = pick(draft, op.ids ?? []);
      if (typeof found === 'string') {
        return { ok: false, reason: `No element "${found}".` };
      }
      if (found.length < 3) {
        return {
          ok: false,
          reason: 'Distribute needs at least three elements.',
        };
      }
      const horizontal = op.axis === 'horizontal';
      if (!horizontal && op.axis !== 'vertical') {
        return { ok: false, reason: `Unknown axis "${op.axis}".` };
      }
      const start = (e: LabelDraftElement) => (horizontal ? e.xIn : e.yIn);
      const len = (e: LabelDraftElement) => (horizontal ? e.wIn : e.hIn);
      const sorted = [...found].sort((a, b) => start(a) - start(b));
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const span = start(last) + len(last) - start(first);
      const gap =
        (span - sorted.reduce((sum, e) => sum + len(e), 0)) /
        (sorted.length - 1);
      if (gap < 0) {
        return {
          ok: false,
          reason: 'They overlap — there is no room to space them.',
        };
      }
      const patches = new Map<string, Partial<LabelDraftElement>>();
      let at = start(first);
      for (const e of sorted) {
        patches.set(e.id, horizontal ? { xIn: at } : { yIn: at });
        at += len(e) + gap;
      }
      const res = moveMany(draft, patches, rules);
      if ('reason' in res) return res;
      return {
        ok: true,
        draft: res.draft,
        applied: { op, elementId: sorted[0].id, elementIds: op.ids },
      };
    }
    case 'reorder_element': {
      const el = els.find((e) => e.id === op.id);
      if (!el) return { ok: false, reason: `No element "${op.id}".` };
      if (op.to !== 'front' && op.to !== 'back') {
        return { ok: false, reason: 'to must be front or back.' };
      }
      const rest = els.filter((e) => e.id !== op.id);
      return {
        ok: true,
        draft: withElements(op.to === 'front' ? [...rest, el] : [el, ...rest]),
        applied: { op, elementId: op.id, elementIds: [op.id] },
      };
    }
    case 'set_stock': {
      const stock = rules.stocks?.find((st) => st.id === op.stockId);
      if (!stock) {
        return { ok: false, reason: `No label stock "${op.stockId}".` };
      }
      if (op.orientation !== 'portrait' && op.orientation !== 'landscape') {
        return {
          ok: false,
          reason: 'orientation must be portrait or landscape.',
        };
      }
      const placed = placeOnLabelStock(draft, stock, op.orientation);
      return {
        ok: true,
        draft: placed,
        applied: { op, elementId: '', elementIds: [] },
      };
    }
    case 'set_barcode': {
      if (!draft.barcode) {
        return { ok: false, reason: "Barcode settings aren't editable here." };
      }
      if (op.which !== 'sku' && op.which !== 'inventory') {
        return { ok: false, reason: 'which must be sku or inventory.' };
      }
      const next = { ...draft.barcode[op.which], ...op.fields };
      for (const k of ['heightPx', 'moduleWidth'] as const) {
        const [lo, hi] = BARCODE_LIMITS[k];
        if (typeof next[k] !== 'number' || next[k] < lo || next[k] > hi) {
          return { ok: false, reason: `${k} must be between ${lo} and ${hi}.` };
        }
      }
      if (typeof next.showText !== 'boolean') {
        return { ok: false, reason: 'showText must be true or false.' };
      }
      return {
        ok: true,
        draft: {
          ...draft,
          barcode: { ...draft.barcode, [op.which]: next },
        },
        applied: { op, elementId: '', elementIds: [] },
      };
    }
    default:
      return { ok: false, reason: 'Unknown operation.' };
  }
}

/**
 * Applies operations in order; each sees the draft as the ones before it left
 * it, and a rejected one is skipped without stopping the rest.
 * @param draft - The draft before
 * @param ops - The operations
 * @param rules - The limits each element is held to
 * @param newId - Mints ids for added elements without one
 * @returns The new draft, what applied, and what didn't (with reasons)
 */
export function applyLabelDraftOps<T extends LabelDraftShape>(
  draft: T,
  ops: LabelDraftOp[],
  rules: LabelDraftRules,
  newId: () => string,
): {
  draft: T;
  applied: AppliedLabelDraftOp[];
  rejected: RejectedLabelDraftOp[];
} {
  let current = draft;
  const applied: AppliedLabelDraftOp[] = [];
  const rejected: RejectedLabelDraftOp[] = [];
  for (const op of ops) {
    const res = applyLabelDraftOp(current, op, rules, newId);
    // `in` rather than `res.ok`: packages compiled without strictNullChecks
    // don't narrow a boolean discriminant.
    if ('reason' in res) {
      rejected.push({ op, reason: res.reason });
    } else {
      current = res.draft;
      applied.push(res.applied);
    }
  }
  return { draft: current, applied, rejected };
}

/** What the warnings check needs to know beyond the draft itself. */
export interface LabelDraftWarningContext {
  /** The plant does USDA or state inspected work. */
  inspected: boolean;
  /** The plant has uploaded an inspection legend. */
  hasLegend: boolean;
}

/** Token names that print a package's weight. */
const WEIGHT_TOKENS = ['WEIGHT', 'WEIGHT_LB'];

/**
 * Problems a processor should hear about after the assistant edits a label —
 * things that print wrong or non-compliant rather than things the validator
 * refuses outright. Covers elements already on the label too (a template
 * predating the rules can sit under a band), so the operator isn't left
 * guessing why something looks off.
 * @param draft - The label after the turn
 * @param ctx - What the plant has (inspection, legend)
 * @param rules - The size limits elements are held to
 * @returns Plain-language warnings, most important first
 */
export function labelDraftWarnings(
  draft: LabelDraftShape,
  ctx: LabelDraftWarningContext,
  rules: LabelDraftRules = DEFAULT_LABEL_DRAFT_RULES,
): string[] {
  const els = draft.layout.elements;
  const warnings: string[] = [];

  const legendBlocks = els.filter(
    (e) => (e.type === 'image' && e.source === 'legend') || e.type === 'mark',
  );
  if (ctx.inspected && legendBlocks.length === 0) {
    warnings.push(
      'No inspection legend on this label — inspected packages need one.',
    );
  }
  if (legendBlocks.some((e) => e.type === 'image') && !ctx.hasLegend) {
    warnings.push(
      "There's a legend block but no legend uploaded, so it prints blank — upload yours in Logos & marks.",
    );
  }

  const showsWeight = els.some(
    (e) =>
      e.type === 'weight' ||
      e.type === 'weightBarcode' ||
      (e.type === 'text' &&
        !!e.template &&
        tokensUsed(e.template).some((t) => WEIGHT_TOKENS.includes(t))),
  );
  if (!showsWeight) warnings.push('No net weight on this label.');

  for (const e of els) {
    const problem = labelElementProblem(e, draft, {
      ...rules,
      knownTokens: undefined,
    });
    if (problem) warnings.push(`${e.type} (${e.id}): ${problem}`);
  }
  const seen = new Set<string>();
  for (const e of els) {
    for (const o of labelElementOverlaps(e, els)) {
      const key = [e.id, o.id].sort().join('|');
      if (seen.has(key)) continue;
      seen.add(key);
      warnings.push(`${e.type} (${e.id}) overlaps ${o.type} (${o.id}).`);
    }
  }
  return warnings;
}
